import React, { useState, useEffect } from 'react';

export default function GrowthCalculator() {
  const [startingBalance, setStartingBalance] = useState<number>(1000);
  const [monthlyBalances, setMonthlyBalances] = useState<number[]>([]);

  const calculateCompoundGrowth = (initial: number) => {
    const dailyRate = 0.02; // 2%
    const daysPerMonth = 30;
    const numberOfMonths = 6;
    
    let balances: number[] = [];
    let currentBalance = initial;
    
    for (let month = 1; month <= numberOfMonths; month++) {
      // Compound daily for each month
      for (let day = 1; day <= daysPerMonth; day++) {
        currentBalance = currentBalance * (1 + dailyRate);
      }
      balances.push(Math.round(currentBalance));
    }
    
    return balances;
  };

  useEffect(() => {
    setMonthlyBalances(calculateCompoundGrowth(startingBalance));
  }, [startingBalance]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setStartingBalance(value);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      <div className="px-6 py-8 bg-primary-500 sm:p-10 sm:pb-6">
        <h3 className="text-2xl leading-9 font-extrabold text-white text-center">
          Growth Calculator
        </h3>
        <p className="mt-4 text-white text-center opacity-90">
          See how your account could grow with a 2% daily profit target*
        </p>
      </div>
      <div className="px-6 pt-6 pb-8 bg-white sm:p-10">
        <div className="space-y-8">
          {/* Interactive Section */}
          <div className="bg-primary-50 p-8 rounded-xl border-2 border-primary-100 text-center">
            <h4 className="text-xl font-bold text-primary-900 mb-6">
              See How Your Balance Could Grow
            </h4>
            <div className="max-w-xs mx-auto">
              <label htmlFor="startingBalance" className="block text-sm font-medium text-primary-700 mb-2">
                Enter Your Starting Balance
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-primary-500 sm:text-sm">£</span>
                </div>
                <input
                  type="number"
                  name="startingBalance"
                  id="startingBalance"
                  className="block w-full pl-7 pr-12 py-3 text-lg border-primary-300 focus:ring-primary-500 focus:border-primary-500 rounded-md"
                  value={startingBalance}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
            </div>
          </div>
          
          {/* Results Section */}
          <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Your Potential Returns</h4>
            <div className="space-y-3">
              {monthlyBalances.map((balance, index) => (
                <div key={index} className="flex justify-between items-center py-3 border-b border-gray-200 last:border-0">
                  <span className="text-gray-600 font-medium">After {index + 1} month{index > 0 ? 's' : ''}:</span>
                  <span className="text-xl font-bold text-primary-600">{formatCurrency(balance)}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-sm text-gray-500 text-center">
            *Calculations assume consistent 2% daily profit and reinvestment of profits. 
            Actual results may vary. Trading involves risk.
          </p>
        </div>
      </div>
    </div>
  );
} 